/*
 * Area "inspection"
 * Owned by one wave-2 agent; see TRANSLATING.md, "Wave 2 partition".
 *
 * One entry per key: key: { en: '...', fr: '...', es: '...' }.
 * New keys are inspection_<screen>_<name>, or the Android key from the translation memory
 * when the English and the meaning are the same (then copy its fr/es verbatim).
 * Look in common.js first. en is required; a missing fr/es falls back to English.
 */
export default {

  // Meter-reading warning (ChecklistWarningUtils), Android keys
  checklist_reading_lower: {
    en: 'This is lower than the last recorded value of {0}. This reading should only go up. Please check the reading before saving.',
    fr: "Cette valeur est inférieure à la dernière valeur enregistrée de {0}. Cette lecture ne devrait qu'augmenter. Vérifiez la lecture avant d'enregistrer.",
    es: 'Este valor es inferior al último valor registrado de {0}. Esta lectura solo debería aumentar. Verifique la lectura antes de guardar.',
  },
  checklist_reading_too_far: {
    en: 'This is {0} more than the last recorded value of {1}, which is more than the {2} allowed for this question. Please check the reading before saving.',
    fr: "Cette valeur dépasse de {0} la dernière valeur enregistrée de {1}, soit plus que les {2} permis pour cette question. Vérifiez la lecture avant d'enregistrer.",
    es: 'Este valor supera en {0} el último valor registrado de {1}, más que los {2} permitidos para esta pregunta. Verifique la lectura antes de guardar.',
  },

  // Schedule and checklist results (components/inspection)
  inspection_note_read_aria: {
    en: 'Read the note for this defect',
    fr: 'Lire la note sur cette défectuosité',
    es: 'Leer la nota sobre este defecto',
  },
  inspection_note_heading: { en: 'ADDITIONAL INFORMATION', fr: 'RENSEIGNEMENTS SUPPLÉMENTAIRES', es: 'INFORMACIÓN ADICIONAL' },
  inspection_col_initial: { en: 'Initial', fr: 'Initiale', es: 'Inicial' },
  inspection_col_enroute: { en: 'Enroute', fr: 'En route', es: 'En ruta' },
  inspection_col_question: { en: 'Question', fr: 'Question', es: 'Pregunta' },
  labelFixed: { en: 'Repaired', fr: 'Réparé', es: 'Reparado' },
  inspection_system_components: { en: 'System and Components', fr: 'Systèmes et composants', es: 'Sistemas y componentes' },
  labelMinorDefects: { en: 'Minor Defect', fr: 'Défectuosité mineure', es: 'Defecto menor' },
  labelMajorDefects: { en: 'Major Defect', fr: 'Défectuosité majeure', es: 'Defecto mayor' },
  inspection_na_button: { en: 'N/A', fr: 'S. O.', es: 'N/A' },
  inspection_defect_detected: { en: 'Defect Detected', fr: 'Défectuosité détectée', es: 'Defecto detectado' },
  inspection_schedule_minor_heading: { en: 'MINOR DEFECTS', fr: 'DÉFECTUOSITÉS MINEURES', es: 'DEFECTOS MENORES' },
  inspection_schedule_major_heading: { en: 'MAJOR DEFECTS', fr: 'DÉFECTUOSITÉS MAJEURES', es: 'DEFECTOS MAYORES' },
  inspection_schedule_no_defects_listed: {
    en: 'No defects are listed for this part.',
    fr: "Aucune défectuosité n'est indiquée pour cette partie.",
    es: 'No hay defectos indicados para esta parte.',
  },
  inspection_fix_button: { en: 'Mark Repaired', fr: 'Marquer réparé', es: 'Marcar reparado' },
  checklist_remarks_value: { en: 'Remarks: {0}', fr: 'Remarques : {0}', es: 'Observaciones: {0}' },
  inspection_schedule_heading: { en: 'Schedule: {0}', fr: 'Annexe : {0}', es: 'Programa: {0}' },
  inspection_checklist_heading: {
    en: 'Checklist: {0} {1}',
    fr: 'Liste de vérification : {0} {1}',
    es: 'Lista de verificación: {0} {1}',
  },

  // Checklist editor (components/checklist). Type and category values are codes; only titles are translated.
  inspection_option_category: { en: 'Category', fr: 'Catégorie', es: 'Categoría' },
  inspection_option_name: { en: 'Option Name', fr: "Nom de l'option", es: 'Nombre de la opción' },
  inspection_item_answer_type: { en: 'Answer Type', fr: 'Type de réponse', es: 'Tipo de respuesta' },
  inspection_item_info_help: {
    en: 'An information block is not asked and not answered. The text below is shown to whoever is filling in the checklist, in this position in the list.',
    fr: "Un bloc d'information n'est ni posé ni répondu. Le texte ci-dessous est affiché à la personne qui remplit la liste de vérification, à cette position dans la liste.",
    es: 'Un bloque de información no se pregunta ni se responde. El texto de abajo se muestra a quien complete la lista de verificación, en esta posición de la lista.',
  },
  inspection_item_text_help: {
    en: 'No options needed. The user will be given a text field to type in.',
    fr: "Aucune option nécessaire. L'utilisateur disposera d'un champ de texte pour écrire.",
    es: 'No se necesitan opciones. El usuario tendrá un campo de texto para escribir.',
  },
  inspection_item_info_placeholder: {
    en: 'Type the text a driver should read here, and use the buttons above to format it.',
    fr: 'Saisissez ici le texte que le conducteur doit lire et utilisez les boutons ci-dessus pour le mettre en forme.',
    es: 'Escriba aquí el texto que el conductor debe leer y use los botones de arriba para darle formato.',
  },
  inspection_item_info_empty: {
    en: 'Enter some text. An empty information block shows nothing at all.',
    fr: "Saisissez du texte. Un bloc d'information vide n'affiche rien du tout.",
    es: 'Escriba algún texto. Un bloque de información vacío no muestra nada.',
  },
  inspection_item_meter_reading: {
    en: 'This is a meter reading, such as hours',
    fr: "Il s'agit d'une lecture de compteur, comme les heures",
    es: 'Es una lectura de contador, como las horas',
  },
  inspection_item_hours_help: {
    en: 'The reading is kept with the piece of equipment itself and shown in the Hours column on the Equipment and Vehicles pages, so it carries on from one inspection to the next however this question is later reworded. Whoever fills in the inspection is warned when the new reading is lower than the last one, or higher by more than the amount below - usually a typing mistake, or a sign the equipment is due for greasing or service. The warning does not stop the inspection from being saved.',
    fr: "La lecture est conservée avec l'équipement lui-même et affichée dans la colonne Heures des pages Équipement et Véhicules; elle se poursuit donc d'une inspection à l'autre, même si cette question est reformulée plus tard. La personne qui remplit l'inspection est avertie lorsque la nouvelle lecture est inférieure à la dernière, ou supérieure de plus que la valeur ci-dessous - généralement une erreur de frappe, ou le signe que l'équipement doit être graissé ou entretenu. L'avertissement n'empêche pas d'enregistrer l'inspection.",
    es: 'La lectura se guarda con el propio equipo y se muestra en la columna Horas de las páginas Equipos y Vehículos, por lo que continúa de una inspección a la siguiente aunque esta pregunta se redacte de otra forma más adelante. Quien complete la inspección recibe una advertencia cuando la nueva lectura es inferior a la última, o superior en más de la cantidad de abajo - normalmente un error de escritura, o una señal de que el equipo necesita engrase o servicio. La advertencia no impide guardar la inspección.',
  },
  inspection_item_number_warn_help: {
    en: 'The last answer is kept with each piece of equipment. Whoever fills in the inspection is warned when the new reading is lower than the last one, or higher by more than the amount below - usually a typing mistake, or a sign the equipment is due for greasing or service. The warning does not stop the inspection from being saved.',
    fr: "La dernière réponse est conservée avec chaque équipement. La personne qui remplit l'inspection est avertie lorsque la nouvelle lecture est inférieure à la dernière, ou supérieure de plus que la valeur ci-dessous - généralement une erreur de frappe, ou le signe que l'équipement doit être graissé ou entretenu. L'avertissement n'empêche pas d'enregistrer l'inspection.",
    es: 'La última respuesta se guarda con cada equipo. Quien complete la inspección recibe una advertencia cuando la nueva lectura es inferior a la última, o superior en más de la cantidad de abajo - normalmente un error de escritura, o una señal de que el equipo necesita engrase o servicio. La advertencia no impide guardar la inspección.',
  },
  inspection_item_warn_threshold: {
    en: 'Warn when it goes up by more than',
    fr: 'Avertir si elle augmente de plus de',
    es: 'Advertir si aumenta en más de',
  },
  inspection_item_warn_threshold_empty: {
    en: 'Enter an amount above zero. Until you do, no warning is shown.',
    fr: "Saisissez une valeur supérieure à zéro. D'ici là, aucun avertissement n'est affiché.",
    es: 'Escriba una cantidad mayor que cero. Hasta entonces, no se muestra ninguna advertencia.',
  },
  inspection_item_multi_select: {
    en: 'Allow multiple selections',
    fr: 'Permettre plusieurs choix',
    es: 'Permitir varias selecciones',
  },
  inspection_item_add_option: { en: 'Add Option', fr: 'Ajouter une option', es: 'Añadir opción' },
  inspection_item_type_text: { en: 'Text Field (String)', fr: 'Champ texte (chaîne)', es: 'Campo de texto (cadena)' },
  inspection_item_type_number: { en: 'Number Field (Number)', fr: 'Champ numérique (nombre)', es: 'Campo numérico (número)' },
  inspection_item_type_bool: { en: 'Yes / No (Boolean)', fr: 'Oui / Non (booléen)', es: 'Sí / No (booleano)' },
  inspection_item_type_list: { en: 'Dropdown List (List)', fr: 'Liste déroulante (liste)', es: 'Lista desplegable (lista)' },
  inspection_item_type_info: {
    en: 'Information Only (Markdown Text)',
    fr: 'Information seulement (texte Markdown)',
    es: 'Solo información (texto Markdown)',
  },
  inspection_item_type_hours: {
    en: 'Hours (Meter Reading)',
    fr: 'Heures (lecture de compteur)',
    es: 'Horas (lectura de contador)',
  },

  // Question type picker (ChecklistQuestionTypeDialog), opened by Add Question.
  inspection_question_type_title: { en: 'Choose a Question Type', fr: 'Choisir un type de question', es: 'Elegir un tipo de pregunta' },
  inspection_question_type_help: {
    en: 'Pick the kind of answer this question needs. You can change it later from the Answer Type list on the question.',
    fr: 'Choisissez le type de réponse dont cette question a besoin. Vous pourrez le changer plus tard dans la liste Type de réponse de la question.',
    es: 'Elija el tipo de respuesta que necesita esta pregunta. Puede cambiarlo más tarde en la lista Tipo de respuesta de la pregunta.',
  },
  inspection_item_type_text_description: {
    en: 'The answer is typed in, in a few words or more. Use it for notes, a trailer number, or anything that is not a fixed choice.',
    fr: "La réponse est saisie, en quelques mots ou plus. Utilisez-le pour des notes, un numéro de remorque, ou tout ce qui n'est pas un choix fixe.",
    es: 'La respuesta se escribe, en pocas palabras o más. Úselo para notas, un número de remolque o cualquier cosa que no sea una opción fija.',
  },
  inspection_item_type_number_description: {
    en: 'The answer is a number, such as a tire pressure or a fuel level.',
    fr: "La réponse est un nombre, comme une pression des pneus ou un niveau de carburant.",
    es: 'La respuesta es un número, como la presión de un neumático o el nivel de combustible.',
  },
  inspection_item_type_bool_description: {
    en: 'The answer is Yes or No. Yes is recorded as good and No as a minor defect, and you can change the category of either.',
    fr: 'La réponse est Oui ou Non. Oui est enregistré comme bon et Non comme une défectuosité mineure; vous pouvez changer la catégorie de chacun.',
    es: 'La respuesta es Sí o No. Sí se registra como bueno y No como un defecto menor, y puede cambiar la categoría de cualquiera de los dos.',
  },
  inspection_item_type_list_description: {
    en: 'The answer is picked from a list you write. Each choice has its own category, so picking one can record a minor or major defect. It can allow more than one choice.',
    fr: "La réponse est choisie dans une liste que vous rédigez. Chaque choix a sa propre catégorie; en choisir un peut donc enregistrer une défectuosité mineure ou majeure. La liste peut permettre plusieurs choix.",
    es: 'La respuesta se elige de una lista que usted escribe. Cada opción tiene su propia categoría, así que elegir una puede registrar un defecto menor o mayor. Puede permitir más de una selección.',
  },
  inspection_item_type_info_description: {
    en: 'Not a question. Text shown to whoever fills in the checklist at this point in the list, such as instructions or a safety reminder.',
    fr: "Ce n'est pas une question. Du texte affiché à la personne qui remplit la liste de vérification, à cette position dans la liste, comme des instructions ou un rappel de sécurité.",
    es: 'No es una pregunta. Texto que se muestra a quien complete la lista de verificación en esta posición de la lista, como instrucciones o un recordatorio de seguridad.',
  },
  inspection_item_type_hours_description: {
    en: 'The reading on the equipment\'s hour meter. It is kept with the equipment and warns when a reading looks wrong. A checklist can have only one.',
    fr: "La lecture du compteur d'heures de l'équipement. Elle est conservée avec l'équipement et donne un avertissement lorsqu'une lecture semble erronée. Une liste de vérification ne peut en avoir qu'une.",
    es: 'La lectura del contador de horas del equipo. Se guarda con el equipo y avisa cuando una lectura parece incorrecta. Una lista de verificación solo puede tener una.',
  },

  // Inspections table (InspectionDataTable). Filter values are the codes the server reads.
  inspection_minor_defects: { en: 'Minor Defects', fr: 'Défectuosités mineures', es: 'Defectos menores' },
  inspection_major_defects: { en: 'Major Defects', fr: 'Défectuosités majeures', es: 'Defectos mayores' },
  labelOdometer: { en: 'Odometer', fr: 'Odomètre', es: 'Odómetro' },
  labelTrailers: { en: 'Trailers', fr: 'Remorques', es: 'Remolques' },
  labelWorkOrders: { en: 'Work Orders', fr: 'Bons de travail', es: 'Órdenes de trabajo' },
  inspection_inspected_by: { en: 'Inspected By', fr: 'Inspecté par', es: 'Inspeccionado por' },
  inspection_driven_by: { en: 'Driven By', fr: 'Conduit par', es: 'Conducido por' },
  inspection_trip_type: { en: 'Inspection Type', fr: "Type d'inspection", es: 'Tipo de inspección' },
  dailylog_today: { en: 'Today', fr: "Aujourd'hui", es: 'Hoy' },
  veh_repair: { en: 'Repair', fr: 'Réparer', es: 'Reparar' },
  inspection_search: { en: 'Search Inspections', fr: 'Rechercher des inspections', es: 'Buscar inspecciones' },
  inspection_asset_name: { en: 'Asset Name', fr: "Nom de l'actif", es: 'Nombre del activo' },
  inspection_asset: { en: 'Asset', fr: 'Actif', es: 'Activo' },
  inspection_download_print: { en: 'Download/Print', fr: 'Télécharger/Imprimer', es: 'Descargar/Imprimir' },
  inspection_filter_none: { en: 'None', fr: 'Aucun', es: 'Ninguno' },
  inspection_filter_not_set: { en: 'Not Set', fr: 'Non défini', es: 'Sin definir' },
  inspection_filter_any: { en: 'Any', fr: 'Toutes', es: 'Cualquiera' },
  inspection_has_repair: { en: 'Has Repair', fr: 'Réparation', es: 'Con reparación' },
  inspection_sort_created_oldest: {
    en: 'Created Date (Oldest first)',
    fr: "Date de création (plus ancienne d'abord)",
    es: 'Fecha de creación (más antigua primero)',
  },
  inspection_sort_created_newest: {
    en: 'Created Date (Newest first)',
    fr: "Date de création (plus récente d'abord)",
    es: 'Fecha de creación (más reciente primero)',
  },
  inspection_time_yesterday: { en: 'Yesterday', fr: 'Hier', es: 'Ayer' },
  inspection_time_last_2_weeks: { en: 'Last 2 Weeks', fr: '2 dernières semaines', es: 'Últimas 2 semanas' },
  inspection_time_this_month: { en: 'This Month', fr: 'Ce mois-ci', es: 'Este mes' },
  inspection_time_last_month: { en: 'Last Month', fr: 'Le mois dernier', es: 'El mes pasado' },
  inspection_time_last_30_days: { en: 'Last 30 days', fr: '30 derniers jours', es: 'Últimos 30 días' },
  inspection_time_last_2_years: { en: 'Last 2 Years', fr: '2 dernières années', es: 'Últimos 2 años' },

  // Checklists table (ChecklistDataTable). State values are codes.
  inspection_checklist_search: {
    en: 'Search Checklists',
    fr: 'Rechercher des listes de vérification',
    es: 'Buscar listas de verificación',
  },
  inspection_checklist_state_removed: { en: 'Removed', fr: 'Retiré', es: 'Quitado' },
  inspection_checklist_questions: { en: 'Questions', fr: 'Questions', es: 'Preguntas' },
  inspection_checklist_created_by: { en: 'Created By', fr: 'Créé par', es: 'Creado por' },

  // Checklists page and its dialogs (pages/operator/checklist). Checklist names and questions are customer data.
  inspection_checklists_document_title: {
    en: 'Checklists - ReadyChek',
    fr: 'Listes de vérification - ReadyChek',
    es: 'Listas de verificación - ReadyChek',
  },
  inspection_checklist_add: { en: 'Add Checklist', fr: 'Ajouter une liste de vérification', es: 'Añadir lista de verificación' },
  inspection_checklist_update_title: {
    en: 'Update Checklist: {0}',
    fr: 'Mettre à jour la liste de vérification : {0}',
    es: 'Actualizar lista de verificación: {0}',
  },
  inspection_checklist_details: {
    en: 'Checklist Details',
    fr: 'Détails de la liste de vérification',
    es: 'Detalles de la lista de verificación',
  },
  inspection_checklist_details_title: {
    en: 'Checklist Details: {0}',
    fr: 'Détails de la liste de vérification : {0}',
    es: 'Detalles de la lista de verificación: {0}',
  },
  inspection_checklist_name: {
    en: 'Checklist Name',
    fr: 'Nom de la liste de vérification',
    es: 'Nombre de la lista de verificación',
  },
  inspection_checklist_name_hint: {
    en: "e.g., 'Daily Pre-Trip' or 'Crane Inspection'",
    fr: 'p. ex. « Ronde de sécurité quotidienne » ou « Inspection de la grue »',
    es: "p. ej., 'Inspección diaria previa al viaje' o 'Inspección de grúa'",
  },
  inspection_checklist_description_hint: {
    en: "A brief description of this checklist's purpose.",
    fr: "Une brève description de l'objet de cette liste de vérification.",
    es: 'Una breve descripción del propósito de esta lista de verificación.',
  },
  inspection_checklist_items_heading: {
    en: 'CHECKLIST ITEMS',
    fr: 'ÉLÉMENTS DE LA LISTE DE VÉRIFICATION',
    es: 'ELEMENTOS DE LA LISTA DE VERIFICACIÓN',
  },
  inspection_checklist_items_help: {
    en: 'Add and edit the questions for this checklist. Click on an item to see its options.',
    fr: 'Ajoutez et modifiez les questions de cette liste de vérification. Cliquez sur un élément pour voir ses options.',
    es: 'Añada y edite las preguntas de esta lista de verificación. Haga clic en un elemento para ver sus opciones.',
  },
  inspection_checklist_add_question: { en: 'Add Question', fr: 'Ajouter une question', es: 'Añadir pregunta' },
  inspection_checklist_add_another_tip: {
    en: 'To add multiple Checklists in a row check this box.',
    fr: 'Pour ajouter plusieurs listes de vérification à la suite, cochez cette case.',
    es: 'Para añadir varias listas de verificación seguidas, marque esta casilla.',
  },
  inspection_checklist_name_required: {
    en: 'Checklist Name is required.',
    fr: 'Le nom de la liste de vérification est obligatoire.',
    es: 'El nombre de la lista de verificación es obligatorio.',
  },
  inspection_checklist_one_hours_question: {
    en: 'A checklist can only have one Hours question.',
    fr: "Une liste de vérification ne peut avoir qu'une seule question Heures.",
    es: 'Una lista de verificación solo puede tener una pregunta de Horas.',
  },
  inspection_checklist_info_block_empty: {
    en: 'At least one Information block has no text.',
    fr: "Au moins un bloc d'information n'a pas de texte.",
    es: 'Al menos un bloque de información no tiene texto.',
  },
  inspection_checklist_item_missing_name: {
    en: 'At least one Checklist Item is missing a name.',
    fr: "Au moins un élément de la liste de vérification n'a pas de nom.",
    es: 'Al menos un elemento de la lista de verificación no tiene nombre.',
  },
  inspection_checklist_option_missing_name: {
    en: "At least one Option in item '{0}' is missing a name.",
    fr: "Au moins une option de l'élément « {0} » n'a pas de nom.",
    es: "Al menos una opción del elemento '{0}' no tiene nombre.",
  },
  inspection_checklist_save_failed: {
    en: 'Failed to save checklist.',
    fr: "Impossible d'enregistrer la liste de vérification.",
    es: 'No se pudo guardar la lista de verificación.',
  },
  // The checklist save succeeded but the server could not record its frozen version
  // (messageId ChecklistVersionNotSavedId). The edit is kept; saving again records it.
  checklist_version_not_saved: {
    en: 'The checklist was saved, but its version could not be recorded. Save it again.',
    fr: "La liste de vérification a été enregistrée, mais sa version n'a pas pu être consignée. Enregistrez-la de nouveau.",
    es: 'La lista de verificación se guardó, pero no se pudo registrar su versión. Guárdela de nuevo.',
  },
  inspection_checklist_not_found: {
    en: 'Checklist not found.',
    fr: 'Liste de vérification introuvable.',
    es: 'No se encontró la lista de verificación.',
  },
  inspection_checklist_question_number: { en: 'Question {0}', fr: 'Question {0}', es: 'Pregunta {0}' },
  inspection_checklist_hours_meter: {
    en: 'Hours meter reading, kept with the equipment',
    fr: "Lecture du compteur d'heures, conservée avec l'équipement",
    es: 'Lectura del contador de horas, guardada con el equipo',
  },
  inspection_checklist_is_meter: {
    en: 'This is a meter reading',
    fr: "Il s'agit d'une lecture de compteur",
    es: 'Es una lectura de contador',
  },
  inspection_checklist_warn_not_set: {
    en: 'Not set. No warning is shown until an amount above zero is entered.',
    fr: "Non défini. Aucun avertissement n'est affiché tant qu'une valeur supérieure à zéro n'est pas saisie.",
    es: 'No definido. No se muestra ninguna advertencia hasta que se escriba una cantidad mayor que cero.',
  },
  inspection_checklist_restore_title: {
    en: 'Restore Checklist: {0}',
    fr: 'Restaurer la liste de vérification : {0}',
    es: 'Restaurar lista de verificación: {0}',
  },
  inspection_checklist_restore_question: {
    en: 'Are you sure you want to restore this checklist?',
    fr: 'Voulez-vous vraiment restaurer cette liste de vérification?',
    es: '¿Está seguro de que desea restaurar esta lista de verificación?',
  },
  inspection_checklist_restore_details: {
    en: 'This operation will make the checklist "Active" again and it will appear in the App.',
    fr: "Cette opération rendra la liste de vérification « Active » de nouveau et elle apparaîtra dans l'application.",
    es: 'Esta operación volverá a poner la lista de verificación como "Activa" y aparecerá en la aplicación.',
  },
  inspection_checklist_restore_failed: {
    en: 'Failed to restore the checklist.',
    fr: 'Impossible de restaurer la liste de vérification.',
    es: 'No se pudo restaurar la lista de verificación.',
  },
  inspection_checklist_remove_title: {
    en: 'Remove Checklist: {0}',
    fr: 'Retirer la liste de vérification : {0}',
    es: 'Quitar lista de verificación: {0}',
  },
  inspection_checklist_remove_question: {
    en: 'Are you sure you want to remove this checklist?',
    fr: 'Voulez-vous vraiment retirer cette liste de vérification?',
    es: '¿Está seguro de que desea quitar esta lista de verificación?',
  },
  inspection_checklist_remove_details: {
    en: 'This action will make the checklist Inactive and it will no longer appear in the App.',
    fr: "Cette action rendra la liste de vérification inactive et elle n'apparaîtra plus dans l'application.",
    es: 'Esta acción dejará la lista de verificación inactiva y ya no aparecerá en la aplicación.',
  },
  inspection_checklist_remove_hint: {
    en: 'This is recommended if the checklist is no longer used by the company. You can restore it later by changing the filter.',
    fr: "Recommandé si l'entreprise n'utilise plus cette liste de vérification. Vous pourrez la restaurer plus tard en changeant le filtre.",
    es: 'Se recomienda si la empresa ya no usa esta lista de verificación. Puede restaurarla más tarde cambiando el filtro.',
  },
  inspection_checklist_remove_failed: {
    en: 'Failed to remove the checklist.',
    fr: 'Impossible de retirer la liste de vérification.',
    es: 'No se pudo quitar la lista de verificación.',
  },

  // Inspections page (InspectionTableView)
  inspection_document_title: { en: 'Inspections - ReadyChek', fr: 'Inspections - ReadyChek', es: 'Inspecciones - ReadyChek' },
  inspection_alerts: { en: 'Alerts', fr: 'Alertes', es: 'Alertas' },
  inspection_add: { en: 'Add Inspection', fr: 'Ajouter une inspection', es: 'Añadir inspección' },

  // Email and PDF dialogs
  labelSend: { en: 'Send', fr: 'Envoyer', es: 'Enviar' },
  inspection_not_found: { en: 'Inspection not found.', fr: 'Inspection introuvable.', es: 'No se encontró la inspección.' },
  inspection_email_title: {
    en: 'Email Inspection PDF',
    fr: "Envoyer le PDF de l'inspection par courriel",
    es: 'Enviar el PDF de la inspección por correo',
  },
  inspection_email_intro: {
    en: 'This will email the inspection PDF to the specified addresses.',
    fr: "Le PDF de l'inspection sera envoyé par courriel aux adresses indiquées.",
    es: 'Se enviará el PDF de la inspección por correo a las direcciones indicadas.',
  },
  inspection_email_addresses: { en: 'Email Addresses', fr: 'Adresses courriel', es: 'Direcciones de correo electrónico' },
  inspection_email_addresses_help: {
    en: "Add one or more email recipients. Press Enter or 'Add' to add an email to the list.",
    fr: 'Ajoutez un ou plusieurs destinataires. Appuyez sur Entrée ou sur « Ajouter » pour ajouter une adresse à la liste.',
    es: "Añada uno o más destinatarios. Pulse Intro o 'Añadir' para añadir un correo a la lista.",
  },
  inspection_email_delay_note: {
    en: 'Please note that it will take a few minutes for the email to be generated and delivered.',
    fr: 'Veuillez noter que la création et la livraison du courriel prendront quelques minutes.',
    es: 'Tenga en cuenta que el correo tardará unos minutos en generarse y entregarse.',
  },
  inspection_email_invalid: {
    en: 'Please enter a valid email address.',
    fr: 'Veuillez saisir une adresse courriel valide.',
    es: 'Escriba una dirección de correo electrónico válida.',
  },
  inspection_email_duplicate: {
    en: 'This email is already in the list.',
    fr: 'Cette adresse courriel est déjà dans la liste.',
    es: 'Este correo ya está en la lista.',
  },
  inspection_email_none: {
    en: 'Please add at least one email address.',
    fr: 'Veuillez ajouter au moins une adresse courriel.',
    es: 'Añada al menos una dirección de correo electrónico.',
  },
  inspection_email_failed: {
    en: 'Failed to send the email.',
    fr: "Impossible d'envoyer le courriel.",
    es: 'No se pudo enviar el correo.',
  },
  inspection_pdf_title: {
    en: 'Inspection PDF Display',
    fr: "Affichage du PDF de l'inspection",
    es: 'Vista del PDF de la inspección',
  },
  inspection_pdf_position: { en: 'PDF {0} of {1}', fr: 'PDF {0} sur {1}', es: 'PDF {0} de {1}' },
  inspection_pdf_open_all: {
    en: 'Open All in New Tabs',
    fr: 'Tout ouvrir dans de nouveaux onglets',
    es: 'Abrir todo en pestañas nuevas',
  },
  inspection_pdf_none: {
    en: 'This inspection does not have a PDF associated with it.',
    fr: "Aucun PDF n'est associé à cette inspection.",
    es: 'Esta inspección no tiene un PDF asociado.',
  },

  // Remarks and export dialogs. The export progress text comes from the server and stays as it arrives.
  inspection_remarks_title: {
    en: 'Add Inspection Remarks',
    fr: "Ajouter des remarques à l'inspection",
    es: 'Añadir observaciones a la inspección',
  },
  inspection_remarks_intro: {
    en: "Enter the text for the remark you'd like to add.",
    fr: 'Saisissez le texte de la remarque à ajouter.',
    es: 'Escriba el texto de la observación que desea añadir.',
  },
  inspection_remarks_empty: {
    en: 'Remarks cannot be empty.',
    fr: 'Les remarques ne peuvent pas être vides.',
    es: 'Las observaciones no pueden estar vacías.',
  },
  inspection_remarks_save_failed: {
    en: 'Failed to save the remark.',
    fr: "Impossible d'enregistrer la remarque.",
    es: 'No se pudo guardar la observación.',
  },
  inspection_remarks_add_images: {
    en: 'Add Images',
    fr: 'Ajouter des images',
    es: 'Añadir imágenes',
  },
  inspection_remarks_images_hint: {
    en: 'Up to {0} images can be attached.',
    fr: "Vous pouvez joindre jusqu'à {0} images.",
    es: 'Puede adjuntar hasta {0} imágenes.',
  },
  inspection_remarks_images_max: {
    en: 'Only {0} images can be attached to a remark.',
    fr: 'Seules {0} images peuvent être jointes à une remarque.',
    es: 'Solo se pueden adjuntar {0} imágenes a una observación.',
  },
  inspection_remarks_image_unreadable: {
    en: 'That file could not be read as an image.',
    fr: "Ce fichier n'a pas pu être lu comme une image.",
    es: 'No se pudo leer ese archivo como imagen.',
  },
  inspection_remarks_image_remove: { en: 'Remove image', fr: "Retirer l'image", es: 'Quitar imagen' },
  inspection_export_title: { en: 'Export Inspections', fr: 'Exporter les inspections', es: 'Exportar inspecciones' },
  inspection_export_heading: {
    en: 'Export Inspections to CSV',
    fr: 'Exporter les inspections en CSV',
    es: 'Exportar inspecciones a CSV',
  },
  inspection_export_intro: {
    en: 'This will export all inspections matching your current filters to a CSV file.',
    fr: 'Toutes les inspections correspondant à vos filtres actuels seront exportées dans un fichier CSV.',
    es: 'Se exportarán a un archivo CSV todas las inspecciones que coincidan con sus filtros actuales.',
  },
  inspection_export_download_note: {
    en: 'When the operation is finished, a download button will appear to download the generated file.',
    fr: "Une fois l'opération terminée, un bouton apparaîtra pour télécharger le fichier créé.",
    es: 'Cuando termine la operación, aparecerá un botón para descargar el archivo generado.',
  },
  inspection_export_running: {
    en: 'Exporting Inspections...',
    fr: 'Exportation des inspections...',
    es: 'Exportando inspecciones...',
  },
  inspection_export_complete: { en: 'Export Complete!', fr: 'Exportation terminée!', es: '¡Exportación completa!' },
  inspection_export_press_download: {
    en: 'Press the Download button to get your file.',
    fr: 'Appuyez sur le bouton Télécharger pour obtenir votre fichier.',
    es: 'Pulse el botón Descargar para obtener su archivo.',
  },
  inspection_export_invalid_filter: {
    en: 'An invalid filter was provided.',
    fr: "Le filtre fourni n'est pas valide.",
    es: 'El filtro proporcionado no es válido.',
  },
  inspection_export_processing: { en: 'Processing...', fr: 'Traitement...', es: 'Procesando...' },
  inspection_export_failed: {
    en: 'Failed to export inspections.',
    fr: "Impossible d'exporter les inspections.",
    es: 'No se pudieron exportar las inspecciones.',
  },

  // Inspections list dialog (opened from Users/Vehicles/Equipment) and the image viewer
  inspection_create_report: { en: 'Create Report', fr: 'Créer un rapport', es: 'Crear informe' },
  inspection_table_for: { en: 'Inspections for {0}', fr: 'Inspections de {0}', es: 'Inspecciones de {0}' },
  inspection_image_title: { en: 'Inspection Image', fr: "Image de l'inspection", es: 'Imagen de la inspección' },
  inspection_image_position: { en: 'Image {0} of {1}', fr: 'Image {0} sur {1}', es: 'Imagen {0} de {1}' },
  inspection_image_none: {
    en: 'No images were found for this comment.',
    fr: "Aucune image n'a été trouvée pour ce commentaire.",
    es: 'No se encontraron imágenes para este comentario.',
  },
  inspection_image_zoom_in: { en: 'Zoom in', fr: 'Zoom avant', es: 'Acercar' },
  inspection_image_zoom_out: { en: 'Zoom out', fr: 'Zoom arrière', es: 'Alejar' },
  inspection_image_zoom_fit: { en: 'Fit to window', fr: 'Ajuster à la fenêtre', es: 'Ajustar a la ventana' },
  inspection_image_pan_left: { en: 'Move left', fr: 'Déplacer vers la gauche', es: 'Mover a la izquierda' },
  inspection_image_pan_right: { en: 'Move right', fr: 'Déplacer vers la droite', es: 'Mover a la derecha' },
  inspection_image_pan_up: { en: 'Move up', fr: 'Déplacer vers le haut', es: 'Mover hacia arriba' },
  inspection_image_pan_down: { en: 'Move down', fr: 'Déplacer vers le bas', es: 'Mover hacia abajo' },
  inspection_image_zoom_hint: {
    en: 'Scroll to zoom, drag to move.',
    fr: 'Faites défiler pour zoomer, glissez pour déplacer.',
    es: 'Desplácese para acercar, arrastre para mover.',
  },

  // Inspection details / repair dialog (InspectionRepairDialog). Headings are shown upper-cased by the code.
  labelLocation: { en: 'Location', fr: 'Lieu', es: 'Ubicación' },
  inspection_hubometer_label: { en: 'Hubometer', fr: 'Hubodomètre', es: 'Hubodómetro' },
  inspection_trailer_label: { en: 'Trailer', fr: 'Remorque', es: 'Remolque' },
  inspection_comment_details: {
    en: 'Inspection Remarks',
    fr: "Remarques sur l'inspection",
    es: 'Observaciones de la inspección',
  },
  labelSignature: { en: 'Signature', fr: 'Signature', es: 'Firma' },
  inspection_details_title: { en: 'Inspection Details', fr: "Détails de l'inspection", es: 'Detalles de la inspección' },
  inspection_logo_alt: { en: 'ReadyChek Logo', fr: 'Logo de ReadyChek', es: 'Logotipo de ReadyChek' },
  inspection_marked_repaired_by: { en: 'Marked repaired by {0}', fr: 'Marqué comme réparé par {0}', es: 'Marcado como reparado por {0}' },
  inspection_recorded_on: { en: 'Record On {0}', fr: 'Enregistré le {0}', es: 'Registrado el {0}' },
  inspection_work_order_value: { en: 'Work Order: {0}', fr: 'Bon de travail : {0}', es: 'Orden de trabajo: {0}' },
  inspection_inspector_and_driver: { en: 'Inspector and Driver', fr: 'Inspecteur et conducteur', es: 'Inspector y conductor' },
  inspection_inspected_by_value: { en: 'Inspected By: {0}', fr: 'Inspecté par : {0}', es: 'Inspeccionado por: {0}' },
  inspection_driven_by_value: { en: 'Driven By: {0}', fr: 'Conduit par : {0}', es: 'Conducido por: {0}' },
  inspection_driver_not_set: { en: '< Driver Not Set >', fr: '< Conducteur non défini >', es: '< Conductor sin definir >' },
  inspection_schedule_results: {
    en: 'Schedule Inspection Results',
    fr: "Résultats de l'inspection selon l'annexe",
    es: 'Resultados de la inspección según el programa',
  },
  inspection_equipment_details: {
    en: 'Equipment Inspection Details',
    fr: "Détails de l'inspection de l'équipement",
    es: 'Detalles de la inspección del equipo',
  },
  inspection_pre_trip_inspection: {
    en: 'Pre-Trip Inspection',
    fr: 'Inspection avant le départ',
    es: 'Inspección previa al viaje',
  },
  inspection_post_trip_inspection: {
    en: 'Post-Trip Inspection',
    fr: 'Inspection après le trajet',
    es: 'Inspección posterior al viaje',
  },
  inspection_first_inspection: { en: 'First Inspection', fr: 'Première inspection', es: 'Primera inspección' },
  inspection_end_inspection: { en: 'End Inspection', fr: 'Inspection de fin', es: 'Inspección final' },
  inspection_date_time: { en: 'Date & Time', fr: 'Date et heure', es: 'Fecha y hora' },
  inspection_checklist_results: {
    en: 'Checklist Inspection Results',
    fr: "Résultats de l'inspection par liste de vérification",
    es: 'Resultados de la inspección por lista de verificación',
  },
  inspection_vehicle_named: { en: 'Vehicle {0}', fr: 'Véhicule {0}', es: 'Vehículo {0}' },
  inspection_trailer_named: { en: 'Trailer {0}: {1}', fr: 'Remorque {0} : {1}', es: 'Remolque {0}: {1}' },
  inspection_remarks_label: { en: 'Remarks:', fr: 'Remarques :', es: 'Observaciones:' },
  inspection_images_label: { en: 'Images:', fr: 'Images :', es: 'Imágenes:' },
  inspection_additional_remarks: {
    en: 'Additional Remarks or Comments',
    fr: 'Remarques ou commentaires supplémentaires',
    es: 'Observaciones o comentarios adicionales',
  },
  inspection_initial_certification: {
    en: 'Initial Inspection Certification',
    fr: "Certification de l'inspection initiale",
    es: 'Certificación de la inspección inicial',
  },
  inspection_declaration_named: {
    en: 'I {0}, declare that the vehicles listed have been inspected in accordance with {1}.',
    fr: 'Je, {0}, déclare que les véhicules énumérés ont été inspectés conformément à {1}.',
    es: 'Yo, {0}, declaro que los vehículos indicados han sido inspeccionados de acuerdo con {1}.',
  },
  inspection_inspected_on: { en: 'Inspected On: {0}', fr: 'Inspecté le : {0}', es: 'Inspeccionado el: {0}' },
  inspection_certified_on: { en: 'Certified On: {0}', fr: 'Certifié le : {0}', es: 'Certificado el: {0}' },
  inspection_inspector_signature: { en: 'Inspector Signature', fr: "Signature de l'inspecteur", es: 'Firma del inspector' },
  inspection_driver_certification: {
    en: 'Driver Inspection Certification',
    fr: "Certification de l'inspection par le conducteur",
    es: 'Certificación de la inspección del conductor',
  },
  inspection_initial_inspected_by: { en: 'Initial Inspected By', fr: 'Inspection initiale par', es: 'Inspección inicial por' },
  inspection_equipment_inspected_by: {
    en: 'This equipment has been inspected by {0}',
    fr: 'Cet équipement a été inspecté par {0}',
    es: 'Este equipo ha sido inspeccionado por {0}',
  },
  inspection_enroute_certification: {
    en: 'Enroute Inspection Certification',
    fr: "Certification de l'inspection en route",
    es: 'Certificación de la inspección en ruta',
  },
  inspection_enroute_inspected_by: { en: 'Enroute Inspected By', fr: 'Inspection en route par', es: 'Inspección en ruta por' },
  inspection_repairs_heading: {
    en: 'Inspection Repairs',
    fr: "Réparations de l'inspection",
    es: 'Reparaciones de la inspección',
  },
  inspection_repair_position: { en: 'Repair {0} of {1}', fr: 'Réparation {0} sur {1}', es: 'Reparación {0} de {1}' },
  inspection_repaired_by: { en: 'Repaired By', fr: 'Réparé par', es: 'Reparado por' },
  inspection_schedule_repaired_defects: {
    en: 'Schedule Repaired Defects',
    fr: 'Défectuosités réparées (annexe)',
    es: 'Defectos reparados (programa)',
  },
  inspection_checklist_repaired_items: {
    en: 'Checklist Repaired Items',
    fr: 'Éléments réparés (liste de vérification)',
    es: 'Elementos reparados (lista de verificación)',
  },
  inspection_repair_certification: {
    en: 'Repair Certification & Signature',
    fr: 'Certification et signature de la réparation',
    es: 'Certificación y firma de la reparación',
  },
  inspection_repair_certify: {
    en: 'I, {0}, certify that the repairs listed above have been made in a proper manner.',
    fr: 'Je, {0}, certifie que les réparations énumérées ci-dessus ont été effectuées correctement.',
    es: 'Yo, {0}, certifico que las reparaciones indicadas arriba se han realizado de manera adecuada.',
  },
  inspection_sign_repair: { en: 'Sign Repair', fr: 'Signer la réparation', es: 'Firmar la reparación' },
  inspection_saving: { en: 'Saving Inspection...', fr: "Enregistrement de l'inspection...", es: 'Guardando la inspección...' },
  inspection_loading_details: {
    en: 'Loading Inspection Details...',
    fr: "Chargement des détails de l'inspection...",
    es: 'Cargando los detalles de la inspección...',
  },
  inspection_print_download: { en: 'Print / Download', fr: 'Imprimer / Télécharger', es: 'Imprimir / Descargar' },
  inspection_save_repair: { en: 'Save Repair', fr: 'Enregistrer la réparation', es: 'Guardar la reparación' },
  inspection_repair_title: { en: 'Repair Inspection', fr: "Réparer l'inspection", es: 'Reparar la inspección' },
  inspection_could_not_be_found: {
    en: 'Inspection could not be found.',
    fr: "L'inspection est introuvable.",
    es: 'No se pudo encontrar la inspección.',
  },
  inspection_form_errors: {
    en: 'Please correct the errors in the form.',
    fr: 'Veuillez corriger les erreurs du formulaire.',
    es: 'Corrija los errores del formulario.',
  },
  inspection_repair_signature_required: {
    en: 'Signature is required. Please sign the repair.',
    fr: 'La signature est obligatoire. Veuillez signer la réparation.',
    es: 'La firma es obligatoria. Firme la reparación.',
  },
  inspection_save_unknown_error: {
    en: 'An unknown error occurred while saving.',
    fr: "Une erreur inconnue s'est produite lors de l'enregistrement.",
    es: 'Se produjo un error desconocido al guardar.',
  },

  // Add Inspection dialog (InspectionUpdateDialog)
  checklist_previous_value: { en: 'Previous: {0}', fr: 'Précédent : {0}', es: 'Anterior: {0}' },
  inspection_reuse: { en: 'Reuse', fr: 'Réutiliser', es: 'Reutilizar' },
  labelClear: { en: 'Clear', fr: 'Effacer', es: 'Borrar' },
  continueAnyway: { en: 'Continue', fr: 'Continuer', es: 'Continuar' },
  vehicle_add_trailer_title: { en: 'Add Trailer', fr: 'Ajouter une remorque', es: 'Añadir remolque' },
  inspection_new_title: { en: 'New Inspection', fr: 'Nouvelle inspection', es: 'Nueva inspección' },
  inspection_create_title: { en: 'Create Inspection', fr: 'Créer une inspection', es: 'Crear inspección' },
  inspection_create_title_for: {
    en: 'Create Inspection for {0}',
    fr: 'Créer une inspection pour {0}',
    es: 'Crear inspección para {0}',
  },
  inspection_create_intro: {
    en: 'Start with when the inspection happened and who it belongs to. These cannot be changed once the inspection itself is filled in.',
    fr: "Commencez par indiquer quand l'inspection a eu lieu et à qui elle appartient. Ces renseignements ne peuvent plus être modifiés une fois l'inspection remplie.",
    es: 'Empiece por indicar cuándo se hizo la inspección y a quién pertenece. Estos datos no se pueden cambiar una vez completada la inspección.',
  },
  inspection_create_step_when: {
    en: '1. WHEN DID THE INSPECTION HAPPEN?',
    fr: "1. QUAND L'INSPECTION A-T-ELLE EU LIEU?",
    es: '1. ¿CUÁNDO SE HIZO LA INSPECCIÓN?',
  },
  inspection_create_date: { en: 'Date of Inspection', fr: "Date de l'inspection", es: 'Fecha de la inspección' },
  inspection_create_date_help: {
    en: 'Defaults to now. Set it back for an inspection recorded on paper or missed on the day.',
    fr: 'Par défaut, maintenant. Reculez la date pour une inspection consignée sur papier ou oubliée le jour même.',
    es: 'Por defecto, ahora. Cámbiela a una fecha anterior para una inspección registrada en papel u olvidada ese día.',
  },
  inspection_create_step_asset: {
    en: '2. WHICH ASSET WAS INSPECTED?',
    fr: '2. QUEL ACTIF A ÉTÉ INSPECTÉ?',
    es: '2. ¿QUÉ ACTIVO SE INSPECCIONÓ?',
  },
  inspection_create_asset_label: {
    en: 'Select a vehicle or equipment to inspect',
    fr: 'Choisissez un véhicule ou un équipement à inspecter',
    es: 'Seleccione un vehículo o equipo para inspeccionar',
  },
  inspection_create_step_driver: {
    en: '3. WHO WAS THE DRIVER?',
    fr: '3. QUI ÉTAIT LE CONDUCTEUR?',
    es: '3. ¿QUIÉN ERA EL CONDUCTOR?',
  },
  inspection_create_driver_label: { en: 'Select the driver', fr: 'Choisissez le conducteur', es: 'Seleccione el conductor' },
  inspection_create_driver_help: {
    en: 'Leave blank if the asset was inspected but nobody drove it.',
    fr: "Laissez vide si l'actif a été inspecté mais que personne ne l'a conduit.",
    es: 'Déjelo en blanco si el activo se inspeccionó pero nadie lo condujo.',
  },
  inspection_create_other_inspector: {
    en: 'Somebody other than the driver carried out the inspection',
    fr: "Une autre personne que le conducteur a fait l'inspection",
    es: 'Otra persona distinta del conductor hizo la inspección',
  },
  inspection_create_step_inspector: {
    en: 'WHO CARRIED OUT THE INSPECTION?',
    fr: "QUI A FAIT L'INSPECTION?",
    es: '¿QUIÉN HIZO LA INSPECCIÓN?',
  },
  inspection_create_inspector_label: {
    en: 'Who carried out the inspection',
    fr: "Qui a fait l'inspection",
    es: 'Quién hizo la inspección',
  },
  inspection_create_start_over: { en: 'Start Over', fr: 'Recommencer', es: 'Empezar de nuevo' },
  inspection_create_discard_question: {
    en: 'Discard this inspection and start again?',
    fr: 'Abandonner cette inspection et recommencer?',
    es: '¿Descartar esta inspección y empezar de nuevo?',
  },
  inspection_create_discard: { en: 'Discard', fr: 'Abandonner', es: 'Descartar' },
  inspection_create_keep: { en: 'Keep', fr: 'Conserver', es: 'Conservar' },
  inspection_create_driven_by: { en: 'Driven by {0}', fr: 'Conduit par {0}', es: 'Conducido por {0}' },
  inspection_vehicle_details: { en: 'Vehicle Details', fr: 'Détails du véhicule', es: 'Detalles del vehículo' },
  inspection_vin_value: { en: 'VIN: {0}', fr: 'NIV : {0}', es: 'VIN: {0}' },
  inspection_trip_type_heading: { en: 'Trip Type', fr: 'Type de trajet', es: 'Tipo de viaje' },
  inspection_create_schedule_questions: {
    en: 'SCHEDULE QUESTIONS (DEFECT SELECTION)',
    fr: "QUESTIONS DE L'ANNEXE (CHOIX DES DÉFECTUOSITÉS)",
    es: 'PREGUNTAS DEL PROGRAMA (SELECCIÓN DE DEFECTOS)',
  },
  inspection_create_checklist_questions: {
    en: 'CHECKLIST QUESTIONS',
    fr: 'QUESTIONS DE LA LISTE DE VÉRIFICATION',
    es: 'PREGUNTAS DE LA LISTA DE VERIFICACIÓN',
  },
  inspection_create_checklist_name: { en: 'Checklist: {0}', fr: 'Liste de vérification : {0}', es: 'Lista de verificación: {0}' },
  inspection_create_item_remarks: { en: 'Remarks / Comments', fr: 'Remarques / commentaires', es: 'Observaciones / comentarios' },
  inspection_create_attached_trailers: { en: 'ATTACHED TRAILERS', fr: 'REMORQUES ATTELÉES', es: 'REMOLQUES ENGANCHADOS' },
  inspection_create_select_trailer: {
    en: 'Select Trailer to Attach',
    fr: 'Choisissez la remorque à atteler',
    es: 'Seleccione el remolque para enganchar',
  },
  inspection_create_no_trailers: {
    en: 'No trailers attached to this inspection.',
    fr: "Aucune remorque n'est attelée pour cette inspection.",
    es: 'No hay remolques enganchados en esta inspección.',
  },
  inspection_create_trailer_value: { en: 'Trailer: {0}', fr: 'Remorque : {0}', es: 'Remolque: {0}' },
  inspection_create_trailer_schedule_defects: {
    en: 'TRAILER SCHEDULE DEFECTS',
    fr: "DÉFECTUOSITÉS DE L'ANNEXE (REMORQUE)",
    es: 'DEFECTOS DEL PROGRAMA (REMOLQUE)',
  },
  inspection_create_trailer_checklist: {
    en: 'TRAILER CHECKLIST',
    fr: 'LISTE DE VÉRIFICATION DE LA REMORQUE',
    es: 'LISTA DE VERIFICACIÓN DEL REMOLQUE',
  },
  inspection_create_add_image: { en: 'Add Image', fr: 'Ajouter une image', es: 'Añadir imagen' },
  inspection_create_certification: {
    en: 'INSPECTION CERTIFICATION',
    fr: "CERTIFICATION DE L'INSPECTION",
    es: 'CERTIFICACIÓN DE LA INSPECCIÓN',
  },
  inspection_create_driver_certification: {
    en: 'DRIVER CERTIFICATION & SIGNATURE',
    fr: 'CERTIFICATION ET SIGNATURE DU CONDUCTEUR',
    es: 'CERTIFICACIÓN Y FIRMA DEL CONDUCTOR',
  },
  inspection_create_inspector_certification: {
    en: 'INSPECTOR CERTIFICATION & SIGNATURE',
    fr: "CERTIFICATION ET SIGNATURE DE L'INSPECTEUR",
    es: 'CERTIFICACIÓN Y FIRMA DEL INSPECTOR',
  },
  inspection_create_declaration: {
    en: 'I, {0}, declare that the vehicles listed have been inspected in accordance with the schedule.',
    fr: "Je, {0}, déclare que les véhicules énumérés ont été inspectés conformément à l'annexe.",
    es: 'Yo, {0}, declaro que los vehículos indicados han sido inspeccionados de acuerdo con el programa.',
  },
  inspection_create_sign_certification: {
    en: 'Sign Certification',
    fr: 'Signer la certification',
    es: 'Firmar la certificación',
  },
  inspection_create_sign_inspector: { en: 'Sign as Inspector', fr: "Signer en tant qu'inspecteur", es: 'Firmar como inspector' },
  inspection_create_sign_driver: { en: 'Sign as Driver', fr: 'Signer en tant que conducteur', es: 'Firmar como conductor' },
  inspection_create_loading_vehicle: {
    en: 'Loading Vehicle Details...',
    fr: 'Chargement des détails du véhicule...',
    es: 'Cargando los detalles del vehículo...',
  },
  inspection_create_save: { en: 'Save Inspection', fr: "Enregistrer l'inspection", es: 'Guardar la inspección' },
  inspection_create_summary_driver_inspector: {
    en: 'Driver {0}, inspected by {1}',
    fr: 'Conducteur {0}, inspecté par {1}',
    es: 'Conductor {0}, inspeccionado por {1}',
  },
  inspection_create_summary_driver: { en: 'Driver {0}', fr: 'Conducteur {0}', es: 'Conductor {0}' },
  inspection_create_summary_not_driven: {
    en: 'Inspected by {0}, not driven',
    fr: 'Inspecté par {0}, non conduit',
    es: 'Inspeccionado por {0}, sin conducir',
  },
  inspection_create_error_date: {
    en: 'Please choose the date the inspection took place.',
    fr: "Veuillez choisir la date de l'inspection.",
    es: 'Elija la fecha en que se hizo la inspección.',
  },
  inspection_create_error_future: {
    en: 'The inspection date and time cannot be in the future.',
    fr: "La date et l'heure de l'inspection ne peuvent pas être dans le futur.",
    es: 'La fecha y hora de la inspección no pueden estar en el futuro.',
  },
  inspection_create_error_asset: {
    en: 'Please choose the asset that was inspected.',
    fr: "Veuillez choisir l'actif inspecté.",
    es: 'Elija el activo que se inspeccionó.',
  },
  inspection_create_error_inspector_untick: {
    en: 'Please choose who carried out the inspection, or untick the box.',
    fr: "Veuillez choisir qui a fait l'inspection, ou décochez la case.",
    es: 'Elija quién hizo la inspección o desmarque la casilla.',
  },
  inspection_create_error_inspector: {
    en: 'Please choose who carried out the inspection.',
    fr: "Veuillez choisir qui a fait l'inspection.",
    es: 'Elija quién hizo la inspección.',
  },
  inspection_create_error_needs_answer: {
    en: '{0}: "{1}" needs an answer.',
    fr: '{0} : « {1} » doit avoir une réponse.',
    es: '{0}: "{1}" necesita una respuesta.',
  },
  inspection_create_error_needs_value: {
    en: '{0}: "{1}" needs a value.',
    fr: '{0} : « {1} » doit avoir une valeur.',
    es: '{0}: "{1}" necesita un valor.',
  },
  inspection_create_this_asset: { en: 'This asset', fr: 'Cet actif', es: 'Este activo' },
  inspection_create_error_vehicle_load: {
    en: 'Vehicle details could not be loaded.',
    fr: 'Impossible de charger les détails du véhicule.',
    es: 'No se pudieron cargar los detalles del vehículo.',
  },
  inspection_create_error_vehicle_info: {
    en: 'Error loading vehicle information: {0}',
    fr: 'Erreur lors du chargement des renseignements du véhicule : {0}',
    es: 'Error al cargar la información del vehículo: {0}',
  },
  inspection_create_error_odometer: {
    en: 'Odometer reading is required.',
    fr: "La lecture de l'odomètre est obligatoire.",
    es: 'La lectura del odómetro es obligatoria.',
  },
  inspection_create_error_location: {
    en: 'Location is required.',
    fr: 'Le lieu est obligatoire.',
    es: 'La ubicación es obligatoria.',
  },
  inspection_create_error_date_invalid: {
    en: 'The inspection date and time is not valid. Use Start Over to set it again.',
    fr: "La date et l'heure de l'inspection ne sont pas valides. Utilisez Recommencer pour les définir de nouveau.",
    es: 'La fecha y hora de la inspección no son válidas. Use Empezar de nuevo para volver a definirlas.',
  },
  inspection_create_error_inspector_signature: {
    en: "The inspector's signature is required. Please sign the inspector certification.",
    fr: "La signature de l'inspecteur est obligatoire. Veuillez signer la certification de l'inspecteur.",
    es: 'La firma del inspector es obligatoria. Firme la certificación del inspector.',
  },
  inspection_create_error_driver_signature: {
    en: "The driver's signature is required. Please sign the certification for {0}, or clear the driver.",
    fr: 'La signature du conducteur est obligatoire. Veuillez signer la certification pour {0}, ou retirez le conducteur.',
    es: 'La firma del conductor es obligatoria. Firme la certificación de {0} o quite el conductor.',
  },
  inspection_create_error_preparing: {
    en: 'An error occurred while preparing the inspection: {0}',
    fr: "Une erreur s'est produite lors de la préparation de l'inspection : {0}",
    es: 'Se produjo un error al preparar la inspección: {0}',
  },
};
